import { defineStore } from 'pinia'
import { ref } from 'vue'

import { api } from '@/libs/api'
import i18n from '@/libs/i18n'
import type { IMemory } from '@/types/Memory'
import type { IStay } from '@/types/Stay'

/**
 * The portal's whole state: one stay, and the look of the house it belongs to.
 *
 * One read feeds every page — the stay is small, and a portal that fetched per page
 * would show a guest four spinners for one fact.
 */

/** Mixes a #rrggbb colour toward white by the given share. */
const lighten = (hex: string, share: number): string => {
    const match = /^#([0-9a-f]{6})$/i.exec(hex.trim())

    if (!match) return hex

    const value = parseInt(match[1]!, 16)
    const mix = (channel: number) => Math.round(channel + (255 - channel) * share)
    const r = mix((value >> 16) & 255)
    const g = mix((value >> 8) & 255)
    const b = mix(value & 255)

    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}

export const useAppStore = defineStore('app', () => {
    const stay    = ref<IStay | null>(null)
    const loading = ref(false)
    const failed  = ref(false)

    /**
     * Which of the link's two lives this is (docs/POTEK_GOSTA.md §9b).
     *
     * 'memory' is a link whose ordinary validity has run out: the stay is not loaded
     * and cannot be, because the service answers nothing but the mode on that door and
     * refuses every other one. Everything the portal draws from `stay` is therefore
     * absent rather than blank, which is the point — a screen cannot half-render a
     * stay it was never given.
     */
    const mode   = ref('stay')
    const memory = ref<IMemory | null>(null)

    /**
     * The one read in flight, if there is one.
     *
     * Two screens have always started this read on the same paint — App.vue mounts and
     * so does the page under it — and a second request was merely wasteful while both
     * answers were the same stay. It stopped being merely wasteful with the memories:
     * in that mode `stay` is deliberately left null, so every `if (!appStore.stay)` in
     * the portal asks again, every time, for ever.
     *
     * The in-flight read is HANDED BACK rather than dropped, because two callers await
     * it for different reasons — a screen filling itself in, and a write refreshing
     * what it just changed (Payments.vue) — and a caller that returned early would
     * redraw from the state it was trying to replace.
     */
    let inFlight: Promise<void> | null = null

    const load = async (): Promise<void> => {
        if (inFlight) return inFlight

        inFlight = read()

        try {
            await inFlight
        } finally {
            inFlight = null
        }
    }

    const read = async () => {
        loading.value = true

        const { status, data } = await api<IStay>()

        if (status !== 200 || !data) {
            loading.value = false
            failed.value = true

            return
        }

        failed.value = false
        mode.value = data.mode || 'stay'

        if (mode.value === 'memory') {
            // Nothing of the stay is kept: the answer carries nothing of it, and a
            // half-filled IStay in this ref would be an invitation for a screen to
            // draw one.
            stay.value = null

            await loadMemory()

            loading.value = false

            return
        }

        loading.value = false
        stay.value = data

        applyAppearance(data)
    }

    /**
     * The memory itself, and the house's own look taken from it.
     *
     * The appearance is applied from HERE and not from applyAppearance below, because
     * the two payloads are deliberately different structures: the memory carries the
     * house's face and nothing else, and a shared function would be the seam through
     * which a stay field found its way onto this page.
     */
    const loadMemory = async () => {
        const { status, data } = await api<IMemory>('/memory')

        if (status !== 200 || !data) {
            failed.value = true

            return
        }

        memory.value = data

        const root = document.documentElement

        if (data.house.primary) {
            root.style.setProperty('--n', data.house.primary)
            root.style.setProperty('--link', data.house.primary)
            root.style.setProperty('--link-light', lighten(data.house.primary, 0.55))
        }

        if (data.house.accent) root.style.setProperty('--o', data.house.accent)

        document.title = data.house.name || 'Imprion Guest'

        const known = ['sl', 'en', 'de', 'it', 'hr', 'hu']

        if (!localStorage.getItem('locale') && known.includes(data.language)) {
            ;(i18n.global.locale as unknown as { value: string }).value = data.language
        }
    }

    /**
     * "Prosim račun na e-pošto".
     *
     * Nothing is sent with it and nothing may be: the recipient is the address already
     * on the reservation, which this portal never holds and never shows. What the
     * screen says afterwards is that it went to the address the guest gave the hotel.
     */
    const mailInvoice = async (): Promise<boolean> => {
        const { status } = await api('/memory/invoice', { method: 'POST', body: JSON.stringify({}) })

        return status === 200
    }

    /**
     * Paints the portal in the house's colours — onto the same variables the
     * prototype's customizer wrote, --n and --o, so the approved stylesheet needs no
     * translation layer.
     */
    const applyAppearance = (data: IStay) => {
        const portal = data.property.portal
        const root = document.documentElement

        if (portal.primary) {
            root.style.setProperty('--n', portal.primary)

            // The links wear the same colour as the bar: the primary itself on the
            // light theme, and on the dark one the same hue lifted toward white far
            // enough to read on a near-black card.
            root.style.setProperty('--link', portal.primary)
            root.style.setProperty('--link-light', lighten(portal.primary, 0.55))
        }
        if (portal.accent) root.style.setProperty('--o', portal.accent)

        document.body.classList.toggle('compact', portal.radius === 'square')

        // An explicit house theme holds only until the guest chooses their own.
        if (!localStorage.getItem('imprionGuestTheme') && (portal.theme === 'dark' || portal.theme === 'light')) {
            document.body.classList.toggle('dark', portal.theme === 'dark')
        }

        document.title = portal.display_name || data.property.name || 'Imprion Guest'

        // Open in the guest's own language — the one their citizenship points at
        // (default_language), and the house's where they have none. Only until the
        // guest picks one: a stored 'locale' is their choice and wins.
        const known = ['sl', 'en', 'de', 'it', 'hr', 'hu']

        if (!localStorage.getItem('locale') && known.includes(data.default_language)) {
            ;(i18n.global.locale as unknown as { value: string }).value = data.default_language
        }
    }

    /** The four facts a guest may write. Answers whether the hotel took them. */
    const savePreferences = async (body: {
        expected_arrival?: string
        contact_phone?: string
        special_requests?: string
        vehicle_plate?: string
        clear_vehicle_plate?: boolean
    }): Promise<boolean> => {
        const { status } = await api('/preferences', { method: 'POST', body: JSON.stringify(body) })

        if (status === 200) await load()

        return status === 200
    }

    const requestLateCheckout = async (): Promise<boolean> => {
        const { status } = await api('/late-checkout', { method: 'POST', body: JSON.stringify({ note: '' }) })

        if (status === 200) await load()

        return status === 200
    }

    /**
     * "Zapustil sem sobo" — the guest saying the room is empty.
     *
     * It is the second half of a departure and touches nothing financial: the bill was
     * settled at the desk (docs/HOUSEKEEPING.md §16). What it does is let the morning's
     * work on that room begin, which is why it is worth a button at all — the guest is
     * the person who actually knows, and they are standing in the corridor.
     */
    const releaseRoom = async (): Promise<boolean> => {
        const { status } = await api('/room-released', { method: 'POST', body: JSON.stringify({}) })

        if (status === 200) await load()

        return status === 200
    }

    return {
        stay, loading, failed, mode, memory,
        load, loadMemory, mailInvoice, savePreferences, requestLateCheckout, releaseRoom,
    }
})
